import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { graphql, createFragmentContainer } from 'react-relay';
import { removeAndApplyFilter, batchRemoveAndApplyFilter } from '../../actions/filterActions';
import { getFilterValues } from '../SbSharedRefineMenu/sbSharedRefineMenuHelpers';
import { trackSearchWithinResults } from '../../utils/tracking/searchBrowse/filterTracking';
import {
    getValidRespAppliedFilters,
    isBatchAppliedFilter,
} from '../SbRespRefineMenu/SbRespRefineMenuFilters/sbRespRefineMenuFiltersMap';

// components
import { FormattedMessage } from 'react-intl';
import { SbRespAppliedFiltersItem as SbSharedAppliedFiltersItem } from './SbRespAppliedFiltersItem';
import { SbSharedMeasurementUnitProvider } from '../SbSharedMeasurementUnitProvider/SbSharedMeasurementUnitProvider';
import { SbSharedRefineMenuSearchWithin } from '../SbSharedRefineMenu/SbSharedRefineMenuSearchWithin/SbSharedRefineMenuSearchWithin';

// styles
import styles from './SbRespAppliedFilters.scss';

export const SbRespAppliedFiltersComponent = props => {
    const {
        itemSearch,
        currency,
        onCloseClick,
        onBatchCloseClick,
        onClearAllClick,
        viewer,
        user,
    } = props;
    const { isSbSharedRefineMenuSearchWithinEnabled } = viewer;
    const { appliedFilters, filters, totalResults } = itemSearch;

    const validAppliedFilters = getValidRespAppliedFilters({ appliedFilters });

    if (!validAppliedFilters.length) {
        return (
            <div className={styles.container}>
                <div className={styles.headerContainer}>
                    <span className={styles.header}>
                        <FormattedMessage
                            id="sb.SbRespAppliedFilters.noResults"
                            defaultMessage="Filter Results"
                        />
                    </span>
                </div>
                {isSbSharedRefineMenuSearchWithinEnabled && (
                    <SbSharedRefineMenuSearchWithin
                        horizontalSpacing="small"
                        onSubmit={trackSearchWithinResults}
                        itemSearch={itemSearch}
                    />
                )}
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.headerContainer}>
                <span className={styles.header}>
                    <FormattedMessage
                        id="sb.SbRespAppliedFilters.title"
                        defaultMessage="Applied Filters"
                    />
                </span>
                <span className={styles.headerResults}>
                    <FormattedMessage
                        id="sb.SbRespAppliedFilters.results"
                        defaultMessage={`
                        {totalResults, plural,
                            one {# result}
                            other {# results}
                        }
                    `}
                        values={{ totalResults }}
                    />
                </span>
            </div>
            <div className={styles.appliedFilters} data-tn="applied-filters-container">
                <SbSharedMeasurementUnitProvider user={user} itemSearch={itemSearch}>
                    {({ measurementUnit }) =>
                        validAppliedFilters.map(appliedFilter => {
                            if (isBatchAppliedFilter(appliedFilter.name)) {
                                return (
                                    <SbSharedAppliedFiltersItem
                                        currency={currency}
                                        key={appliedFilter.name}
                                        filterName={appliedFilter.name}
                                        localizedFilterName={appliedFilter.localizedFilterName}
                                        availableValues={getFilterValues(
                                            filters,
                                            appliedFilter.name
                                        )}
                                        onCloseClick={() => onBatchCloseClick(appliedFilter.name)}
                                    />
                                );
                            }

                            return appliedFilter.values.map((filterValue, index) => (
                                <SbSharedAppliedFiltersItem
                                    currency={currency}
                                    filterValue={filterValue}
                                    key={`${appliedFilter.name}-${index}`}
                                    filterName={appliedFilter.name}
                                    localizedFilterName={appliedFilter.localizedFilterName}
                                    availableValues={getFilterValues(filters, appliedFilter.name)}
                                    onCloseClick={() =>
                                        onCloseClick(appliedFilter.name, filterValue)
                                    }
                                    measurementUnit={measurementUnit}
                                />
                            ));
                        })
                    }
                </SbSharedMeasurementUnitProvider>
            </div>
            {isSbSharedRefineMenuSearchWithinEnabled && (
                <SbSharedRefineMenuSearchWithin
                    itemSearch={itemSearch}
                    onSubmit={trackSearchWithinResults}
                />
            )}
            <span
                className={styles.clearAllButton}
                data-tn="applied-filters-clear-all"
                onClick={() => onClearAllClick(validAppliedFilters.map(({ name }) => name))}
            >
                <FormattedMessage
                    id="sb.SbRespAppliedFilters.clearAll"
                    defaultMessage="Clear All"
                />
            </span>
        </div>
    );
};

SbRespAppliedFiltersComponent.defaultProps = {
    onCloseClick: () => {},
    onBatchCloseClick: () => {},
    onClearAllClick: () => {},
};

SbRespAppliedFiltersComponent.propTypes = {
    user: PropTypes.object,
    viewer: PropTypes.object.isRequired,
    currency: PropTypes.string.isRequired,
    itemSearch: PropTypes.object.isRequired,
    // redux
    onCloseClick: PropTypes.func.isRequired,
    onBatchCloseClick: PropTypes.func.isRequired,
    onClearAllClick: PropTypes.func.isRequired,
};

function mapDispatchToProps(dispatch) {
    return {
        onCloseClick(filterName, filterValue) {
            dispatch(removeAndApplyFilter(filterName, filterValue));
        },
        onBatchCloseClick(filterName) {
            dispatch(batchRemoveAndApplyFilter([filterName]));
        },
        onClearAllClick(filterNames) {
            dispatch(batchRemoveAndApplyFilter(filterNames));
        },
    };
}

export const SbRespAppliedFilters = createFragmentContainer(
    connect(
        null,
        mapDispatchToProps
    )(SbRespAppliedFiltersComponent),
    {
        viewer: graphql`
            fragment SbRespAppliedFilters_viewer on Viewer {
                isSbSharedRefineMenuSearchWithinEnabled: isEnabledByLocale(
                    featureName: "NON_MVP_SEARCH_BROWSE"
                )
            }
        `,
        itemSearch: graphql`
            fragment SbRespAppliedFilters_itemSearch on ItemSearchQueryConnection {
                ...SbSharedRefineMenuSearchWithin_itemSearch
                ...SbSharedMeasurementUnitProvider_itemSearch
                totalResults
                filters {
                    name
                    localizedFilterName
                    values {
                        displayName
                        urlLabel
                    }
                }
                appliedFilters {
                    name
                    localizedFilterName
                    canBeDismissed
                    values {
                        urlLabel
                        displayName
                        hexCode
                    }
                }
            }
        `,
        user: graphql`
            fragment SbRespAppliedFilters_user on User {
                ...SbSharedMeasurementUnitProvider_user
            }
        `,
    }
);
